import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRazorpay } from 'react-razorpay';
import MainLayout from '../components/layout/MainLayout';
import { showAppError } from '../services/messageDialogService';
import AppHeader from '../components/layout/AppHeader';
import BazaarCanopy from '../components/bazzar/BazaarCanopy';
import BazaarTopTabs from '../components/bazzar/BazaarTopTabs';
import ConfirmRedemptionDialog from '../components/bazzar/ConfirmRedemptionDialog';
import CouponUnavailableDialog from '../components/bazzar/CouponUnavailableDialog';
import MartInsufficientXpDialog from '../components/bazzar/MartInsufficientXpDialog';
import DiscountBadge from '../components/bazzar/DiscountBadge';
import PassesPanel from '../components/bazzar/PassesPanel';
import ProductCard from '../components/bazzar/ProductCard';
import PassKnowMoreBar from '../components/bazzar/PassKnowMoreBar';
import PassKnowMoreDialog from '../components/bazzar/PassKnowMoreDialog';
import RapCoinKnowMoreDialog from '../components/bazzar/RapCoinKnowMoreDialog';
import RapGemKnowMoreDialog from '../components/bazzar/RapGemKnowMoreDialog';
import VipRoomKnowMoreDialog from '../components/bazzar/VipRoomKnowMoreDialog';
import PromoStrip from '../components/bazzar/PromoStrip';
import PurchaseSuccessDialog from '../components/bazzar/PurchaseSuccessDialog';
import RedemptionSuccessDialog from '../components/bazzar/RedemptionSuccessDialog';
import VipPassTitle from '../components/bazzar/VipPassTitle';
import XpPanel from '../components/bazzar/XpPanel';
import {
  BAZAAR_IAP_TYPES,
  computeMartRewardProgress,
  fetchBazaarIaps,
  fetchMartHome,
  fetchPassCatalog,
  getApiErrorMessage,
  isCouponUnavailableError,
  PASS_INTERVAL_ORDER,
  redeemMartReward,
} from '../services/bazaarService';
import { fetchUserProfile } from '../services/authService';
import {
  trackPassPurchaseConfirm,
  trackPassPurchaseResult,
  trackPassPurchaseTap,
  trackPurchaseConfirm,
  trackPurchaseResult,
  trackPurchaseTap,
  trackRedeemConfirm,
  trackRedeemResult,
  trackRedeemTap,
} from '../services/bazaarAnalytics';
import { getPaymentErrorMessage, initiateRazorpayCheckout } from '../services/paymentService';
import { clearAuthSession } from '../storage/authStorage';
import { getTotalXp, getUserProfile } from '../storage/userProfileStorage';
import { BAZZAR_MOBILE_PATH } from '../routes/paths';

const storeTabs = ['rapcoin', 'rapgem', 'vip-room'];

function resolveBazaarTopTab(state) {
  const tab = state?.topTab;
  return tab === 'mart' || tab === 'store' || tab === 'passes' ? tab : 'mart';
}

function resolveBazaarStoreTab(state) {
  const tab = state?.storeTab;
  return storeTabs.includes(tab) ? tab : 'rapcoin';
}

export default function BazzarPage() {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const hideHeader = pathname === BAZZAR_MOBILE_PATH;
  const { Razorpay, isLoading: razorpayScriptLoading, error: razorpayScriptError } = useRazorpay();
  const [topTab, setTopTab] = useState(() => resolveBazaarTopTab(location.state));
  const [storeTab, setStoreTab] = useState(() => resolveBazaarStoreTab(location.state));
  const [selectedCard, setSelectedCard] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [redemptionSuccessOpen, setRedemptionSuccessOpen] = useState(false);
  const [redemptionResult, setRedemptionResult] = useState(null);
  const [insufficientXpOpen, setInsufficientXpOpen] = useState(false);
  const [couponUnavailableOpen, setCouponUnavailableOpen] = useState(false);
  const [couponUnavailableResult, setCouponUnavailableResult] = useState(null);
  const [passKnowMoreOpen, setPassKnowMoreOpen] = useState(false);
  const [rapCoinKnowMoreOpen, setRapCoinKnowMoreOpen] = useState(false);
  const [rapGemKnowMoreOpen, setRapGemKnowMoreOpen] = useState(false);
  const [vipRoomKnowMoreOpen, setVipRoomKnowMoreOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [storeCatalog, setStoreCatalog] = useState({
    rapcoin: [],
    rapgem: [],
    'vip-room': [],
  });
  const [gemPassCatalog, setGemPassCatalog] = useState([]);
  const [vipPassCatalog, setVipPassCatalog] = useState([]);
  const [martCatalog, setMartCatalog] = useState([]);
  const [availableXp, setAvailableXp] = useState(() => getTotalXp());
  const [profileXp, setProfileXp] = useState(() => getTotalXp());
  const [xpExpirationLabel, setXpExpirationLabel] = useState('XPs have 7 day validity. Redeem Now!');

  useEffect(() => {
    const { topTab: navTopTab, storeTab: navStoreTab } = location.state ?? {};
    if (navTopTab === 'mart' || navTopTab === 'store' || navTopTab === 'passes') {
      setTopTab(navTopTab);
    }
    if (storeTabs.includes(navStoreTab)) {
      setStoreTab(navStoreTab);
    }
  }, [location.key, location.state]);

  const reloadMartHome = useCallback(async () => {
    const { availableXP, xpExpirationLabel: label, items } = await fetchMartHome();
    setMartCatalog(items);
    setAvailableXp(availableXP);
    setProfileXp(getTotalXp());
    setXpExpirationLabel(label);
  }, []);

  useEffect(() => {
    if (topTab !== 'mart') return undefined;

    let cancelled = false;

    async function loadMartHome() {
      setCatalogLoading(true);

      try {
        await reloadMartHome();
      } catch (error) {
        if (cancelled) return;

        showAppError(getApiErrorMessage(error, 'Unable to load mart rewards.'));
        setMartCatalog([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }

    loadMartHome();

    return () => {
      cancelled = true;
    };
  }, [topTab, reloadMartHome]);

  useEffect(() => {
    if (topTab !== 'store') return undefined;

    const catalogKey = storeTab;
    let cancelled = false;

    async function loadStoreCatalog() {
      setCatalogLoading(true);

      try {
        const { items } = await fetchBazaarIaps(BAZAAR_IAP_TYPES[catalogKey]);
        if (cancelled) return;

        setStoreCatalog((prev) => ({
          ...prev,
          [catalogKey]: items,
        }));
      } catch (error) {
        if (cancelled) return;

        showAppError(getApiErrorMessage(error, 'Unable to load store items.'));
        setStoreCatalog((prev) => ({
          ...prev,
          [catalogKey]: [],
        }));
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }

    loadStoreCatalog();

    return () => {
      cancelled = true;
    };
  }, [topTab, storeTab]);

  useEffect(() => {
    if (topTab !== 'passes') return undefined;

    let cancelled = false;

    async function loadPassCatalog() {
      setCatalogLoading(true);

      try {
        const { gemPasses, vipPasses } = await fetchPassCatalog();
        if (cancelled) return;

        setGemPassCatalog(gemPasses);
        setVipPassCatalog(vipPasses);
      } catch (error) {
        if (cancelled) return;

        showAppError(getApiErrorMessage(error, 'Unable to load subscription packages.'));
        setGemPassCatalog([]);
        setVipPassCatalog([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }

    loadPassCatalog();

    return () => {
      cancelled = true;
    };
  }, [topTab]);

  const activeStoreCards = storeCatalog[storeTab];
  const activeGemPassCards = gemPassCatalog;
  const activeVipPassCards = vipPassCatalog;

  const currentData = useMemo(() => {
    if (topTab === 'mart') {
      return martCatalog.map((item) => ({
        ...item,
        progress: computeMartRewardProgress(item.raw?.xpValue, profileXp),
        variant: 'mart',
        actionLabel: `${item.cost} - REDEEM`,
      }));
    }

    if (topTab === 'store') {
      return activeStoreCards.map((item) => ({
        ...item,
        variant: 'store',
        actionLabel:
          item.tone === 'coin'
            ? item.price
            : item.tone?.includes('gem')
              ? `Buy- ${item.price}`
              : `Buy - ${item.price}`,
      }));
    }

    return [];
  }, [topTab, martCatalog, profileXp, activeStoreCards]);

  const gemPassCards = useMemo(
    () =>
      [...activeGemPassCards]
        .sort(
          (a, b) =>
            (PASS_INTERVAL_ORDER[a.interval] ?? 99) - (PASS_INTERVAL_ORDER[b.interval] ?? 99),
        )
        .map((item) => ({
          ...item,
          variant: 'passes',
          actionLabel: item.priceLabel || item.price,
        })),
    [activeGemPassCards],
  );

  const vipPassCards = useMemo(
    () =>
      activeVipPassCards.map((item) => ({
        ...item,
        variant: 'passes',
        actionLabel: item.priceLabel || item.price,
      })),
    [activeVipPassCards],
  );

  const showCatalogEmpty = !catalogLoading && topTab !== 'passes' && currentData.length === 0;
  const showVipPassEmpty = topTab === 'passes' && !catalogLoading && vipPassCards.length === 0;

  useEffect(() => {
    if (razorpayScriptError) {
      showAppError('Unable to load Razorpay checkout.');
    }
  }, [razorpayScriptError]);

  const handleCardClick = (card) => {
    if (card?.variant === 'mart') {
      trackRedeemTap(card, availableXp);
    } else if (card?.variant === 'store') {
      trackPurchaseTap(card, storeTab);
    } else if (card?.variant === 'passes') {
      trackPassPurchaseTap(card);
    }

    setSelectedCard(card);
    setConfirmOpen(true);
  };

  const handleOpenLogoutDialog = () => {
    setLogoutDialogOpen(true);
  };

  const handleCloseLogoutDialog = () => {
    setLogoutDialogOpen(false);
  };

  const handleConfirmLogout = () => {
    clearAuthSession();
    setLogoutDialogOpen(false);
    navigate('/', { replace: true });
  };

  const handleConfirmPurchase = async () => {
    const card = selectedCard;
    if (!card) return;

    if (card.variant === 'mart') {
      const redeemingXp = Number(card?.xpValue) || 0;
      if (redeemingXp > availableXp) {
        setConfirmOpen(false);
        setInsufficientXpOpen(true);
        return;
      }

      trackRedeemConfirm(card);
      setConfirmLoading(true);

      try {
        const rewardId = card.rewardId ?? card.raw?.id ?? card.raw?._id ?? card.id;
        const result = await redeemMartReward(rewardId);

        if (result?.ok === false) {
          await fetchUserProfile().catch(() => null);
          await reloadMartHome();
          trackRedeemResult(card, result);

          if (isCouponUnavailableError(result)) {
            setCouponUnavailableResult(result);
            setConfirmOpen(false);
            setCouponUnavailableOpen(true);
            return;
          }

          showAppError(result.message || 'Unable to redeem voucher.');
          return;
        }

        await fetchUserProfile().catch(() => null);
        await reloadMartHome();
        trackRedeemResult(card, result);
        setRedemptionResult(result);
        setConfirmOpen(false);
        setRedemptionSuccessOpen(true);
      } catch (error) {
        trackRedeemResult(card, null, error);
        const message = getApiErrorMessage(error, 'Unable to redeem voucher.');
        if (/not enough|insufficient|enough xp/i.test(message)) {
          setConfirmOpen(false);
          setInsufficientXpOpen(true);
        } else {
          showAppError(message);
        }
      } finally {
        setConfirmLoading(false);
      }

      return;
    }

    if (razorpayScriptLoading || !Razorpay) {
      showAppError('Payment gateway is still loading. Please try again.');
      return;
    }

    const isPassPurchase = card.variant === 'passes';
    const wasPremium = Boolean(getUserProfile()?.isPremium);

    if (isPassPurchase) {
      trackPassPurchaseConfirm(card);
    } else {
      trackPurchaseConfirm(card, storeTab);
    }

    setConfirmLoading(true);

    try {
      const verification = await initiateRazorpayCheckout({ Razorpay, card });

      if (verification?.verified === true) {
        await fetchUserProfile().catch(() => null);
        await reloadMartHome().catch(() => null);

        if (topTab === 'store') {
          const { items } = await fetchBazaarIaps(BAZAAR_IAP_TYPES[storeTab]);
          setStoreCatalog((prev) => ({
            ...prev,
            [storeTab]: items,
          }));
        } else if (topTab === 'passes') {
          const { gemPasses, vipPasses } = await fetchPassCatalog();
          setGemPassCatalog(gemPasses);
          setVipPassCatalog(vipPasses);
        }

        const successOutcome = {
          status: 'success',
          paymentMethod: verification?.paymentMethod,
          razorpayPaymentId: verification?.razorpayPaymentId,
          razorpayOrderId: verification?.razorpayOrderId,
        };

        if (isPassPurchase) {
          trackPassPurchaseResult(card, successOutcome, wasPremium);
        } else {
          trackPurchaseResult(card, storeTab, successOutcome);
        }

        setConfirmOpen(false);
        setSuccessOpen(true);
        return;
      }

      throw new Error(verification?.message || 'Payment verification failed.');
    } catch (error) {
      const status = error?.paymentStatus || (getPaymentErrorMessage(error) === 'Payment cancelled.' ? 'cancelled' : 'failed');
      const failOutcome = {
        status,
        paymentMethod: error?.paymentMethod,
        razorpayPaymentId: error?.razorpayPaymentId,
        razorpayOrderId: error?.razorpayOrderId,
        errorReason: status === 'cancelled' ? 'cancelled' : getPaymentErrorMessage(error),
      };

      if (isPassPurchase) {
        trackPassPurchaseResult(card, failOutcome, wasPremium);
      } else {
        trackPurchaseResult(card, storeTab, failOutcome);
      }

      const message = getPaymentErrorMessage(error);
      if (message !== 'Payment cancelled.') {
        showAppError(message);
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <MainLayout>
      {!hideHeader ? (
        <Box sx={{ px: { md: '50px' } }}>
          <AppHeader endVariant="profile" onLogoutClick={handleOpenLogoutDialog} />
        </Box>
      ) : null}
      <BazaarCanopy />

      <Box sx={{ px: { xs: '5px', sm: 1.25, md: 1.5 } }}>
        <BazaarTopTabs value={topTab} onChange={setTopTab} />

        {topTab === 'store' ? (
          <Stack
            direction="row"
            spacing={{ xs: 2.5, md: 0.8 }}
            justifyContent="center"
            sx={{ mb: 1, maxWidth: { xs: 400, md: 'none' }, mx: 'auto', width: '100%' }}
          >
            {storeTabs.map((tabName) => (
              <Chip
                key={tabName}
                label={tabName === 'vip-room' ? 'VIP Rooms' : tabName === 'rapcoin' ? 'RapCoins' : 'RapGems'}
                onClick={() => setStoreTab(tabName)}
                sx={{
                  flex: { xs: 1, md: '0 0 auto' },
                  height: { xs: 40, md: 32 },
                  borderRadius: '22px',
                  color: storeTab === tabName ? '#fff' : '#483046',
                  background: storeTab === tabName ? 'linear-gradient(180deg, #FF00F2 41.566%, #822AFF 100%)' : '#d7c3e2',
                  '& .MuiChip-label': {
                    fontWeight: 600,
                    fontSize: { xs: 16, md: 16 },
                    lineHeight: '24px',
                    letterSpacing: '0.8px',
                    px: { xs: 1, md: 1.8 },
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            ))}
          </Stack>
        ) : null}

        {topTab === 'mart' ? <XpPanel totalXp={availableXp} expirationLabel={xpExpirationLabel} /> : null}

        {topTab !== 'mart' ? (
          <>
            <Box sx={{ display: { xs: 'block', lg: 'none' }, maxWidth: 400, mx: 'auto' }}>
              <PromoStrip tone="green" />
              <Box sx={{ mt: topTab === 'passes' ? 0 : 1.5 }}>
                {topTab === 'passes' ? <VipPassTitle /> : <DiscountBadge storeTab={storeTab} />}
              </Box>
            </Box>
            <Grid container spacing={{ md: 2 }} alignItems="center" sx={{ display: { xs: 'none', lg: 'flex' }, my: topTab === 'passes' ? 0 : 2.2 }}>
              <Grid size={{ md: 4 }}>
                <PromoStrip tone="green" />
              </Grid>
              <Grid size={{ md: 4 }}>{topTab === 'passes' ? <VipPassTitle /> : <DiscountBadge storeTab={storeTab} />}</Grid>
              <Grid size={{ md: 4 }}>
                {topTab === 'passes' ? (
                  <PassKnowMoreBar variant="passes" onKnowMoreClick={() => setPassKnowMoreOpen(true)} />
                ) : topTab === 'store' ? (
                  <PassKnowMoreBar
                    variant={storeTab}
                    onKnowMoreClick={
                      storeTab === 'rapcoin'
                        ? () => setRapCoinKnowMoreOpen(true)
                        : storeTab === 'rapgem'
                          ? () => setRapGemKnowMoreOpen(true)
                          : storeTab === 'vip-room'
                            ? () => setVipRoomKnowMoreOpen(true)
                            : undefined
                    }
                  />
                ) : (
                  <PromoStrip tone="red" />
                )}
              </Grid>
            </Grid>
          </>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: { xs: 0, md: topTab === 'mart' ? '0px' : 1.5 },
            width: '100%',
          }}
        >
          <Box
            sx={
              topTab === 'store'
                ? {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: '5px', md: 3 },
                    rowGap: { xs: '28px', md: 3 },
                    flexWrap: 'wrap',
                  }
                : {
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: { xs: '10px', md: 3 },
                    rowGap: { xs: '28px', md: 3 },
                    flexWrap: 'wrap',
                  }
            }
          >
            {topTab === 'passes' && catalogLoading ? (
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#ff00f2' }} />
              </Box>
            ) : topTab === 'passes' && showVipPassEmpty ? (
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', py: 6 }}>
                <Typography sx={{ color: '#e6d4ff', fontSize: 14, fontWeight: 600 }}>
                  No VIP passes available right now.
                </Typography>
              </Box>
            ) : topTab === 'passes' ? (
              vipPassCards.map((item) => (
                <Box
                  key={`${item.variant}-${item.id}`}
                  sx={{
                    display: 'flex',
                    minWidth: 0,
                    width: '350px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <ProductCard card={item} onClick={handleCardClick} userXp={availableXp} />
                </Box>
              ))
            ) : catalogLoading ? (
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#ff00f2' }} />
              </Box>
            ) : showCatalogEmpty ? (
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', py: 6 }}>
                <Typography sx={{ color: '#e6d4ff', fontSize: 14, fontWeight: 600 }}>
                  No items available right now.
                </Typography>
              </Box>
            ) : (
              currentData.map((item) => (
                <Box
                  key={`${item.variant}-${item.id}`}
                  sx={
                    topTab === 'store'
                    ? {
                      display: 'flex',
                      minWidth: 0,
                      width: '170px',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }
                    : topTab === 'passes'
                    ? {
                      display: 'flex',
                      minWidth: 0,
                      width: '350px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      }
                    : {
                      display: 'flex',
                      minWidth: 0,
                      width: '255px',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }
                  }
                >
                  <ProductCard card={item} onClick={handleCardClick} userXp={availableXp} />
                </Box>
              ))
            )}
          </Box>
        </Box>
        {topTab === 'passes' ? (
          <Box sx={{ mb: { xs: 2, md: 3 }, width: '100%',mt: { xs: 2, md: 3 } }}>
            <PassesPanel cards={gemPassCards} loading={catalogLoading} onCardClick={handleCardClick} />
          </Box>
        ) : null}
      </Box>

      <ConfirmRedemptionDialog
        open={confirmOpen}
        card={selectedCard}
        availableXp={availableXp}
        confirmLoading={confirmLoading}
        onClose={() => {
          if (confirmLoading) return;
          setConfirmOpen(false);
        }}
        onConfirm={handleConfirmPurchase}
      />

      <RedemptionSuccessDialog
        open={redemptionSuccessOpen}
        card={selectedCard}
        redemption={redemptionResult}
        onClose={() => {
          setRedemptionSuccessOpen(false);
          setRedemptionResult(null);
        }}
      />

      <MartInsufficientXpDialog
        open={insufficientXpOpen}
        onClose={() => setInsufficientXpOpen(false)}
      />

      <CouponUnavailableDialog
        open={couponUnavailableOpen}
        result={couponUnavailableResult}
        onClose={() => {
          setCouponUnavailableOpen(false);
          setCouponUnavailableResult(null);
        }}
      />

      <PurchaseSuccessDialog open={successOpen} card={selectedCard} onClose={() => setSuccessOpen(false)} />

      <PassKnowMoreDialog open={passKnowMoreOpen} onClose={() => setPassKnowMoreOpen(false)} />

      <RapCoinKnowMoreDialog open={rapCoinKnowMoreOpen} onClose={() => setRapCoinKnowMoreOpen(false)} />

      <RapGemKnowMoreDialog open={rapGemKnowMoreOpen} onClose={() => setRapGemKnowMoreOpen(false)} />

      <VipRoomKnowMoreDialog open={vipRoomKnowMoreOpen} onClose={() => setVipRoomKnowMoreOpen(false)} />

      <Dialog
        open={logoutDialogOpen}
        onClose={handleCloseLogoutDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#1a0834',
            border: '1px solid #ff66f766',
            color: '#fff',
            minWidth: { xs: '90%', sm: 380 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#fff' }}>Logout</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#e6d4ff' }}>
            Are you sure you want to logout?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseLogoutDialog}
            sx={{ color: '#e6d4ff', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
